import React, { useEffect, useRef, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import PageServices from '../../services/PageServices';
import { formatCompact } from './optionChainShared';

// Future Price Graph popup for the Backtester: the same chart as the Option Chain page
// (future price, spot price, future OI, future OI change per minute), for the selected
// `date` up to the selected `time` (GET /futurePrice?date=&time=). Loads when opened and
// whenever the symbol / date / time changes while it is open. `onLatest` receives the last
// point ({ futurePrice, spotPrice }) so the page can show it in the spot banner.

const timeFormatter = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' });

const mergeByMinute = (future, spot) => {
  const spotByMinute = new Map(spot.map((s) => [Math.floor(new Date(s.timestamp).getTime() / 60000), s]));
  return future.map((f) => {
    const t = new Date(f.timestamp);
    const s = spotByMinute.get(Math.floor(t.getTime() / 60000));
    return {
      time: timeFormatter.format(t),
      futurePrice: f.FutruePrice?.LastTradePrice || 0,
      futureOI: f.FutruePrice?.OpenInterest || 0,
      futureOIChange: f.FutruePrice?.OpenInterestChange || 0,
      spotPrice: s?.spotPrice?.LastTradePrice || 0,
    };
  });
};

const FuturePriceGraphModal = ({ symbol, date, time, modalId = 'futurpricegraph', onLatest }) => {
  const modalRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState([]);
  const [status, setStatus] = useState('idle'); // idle | loading | ready | empty
  const [show, setShow] = useState({ futureOI: true, futurePrice: true, spotPrice: true, futureOIChange: false });
  const onLatestRef = useRef(onLatest);
  onLatestRef.current = onLatest;

  useEffect(() => {
    const modal = modalRef.current;
    if (!modal) return undefined;
    const onShow = () => setOpen(true);
    const onHide = () => setOpen(false);
    modal.addEventListener('show.bs.modal', onShow);
    modal.addEventListener('hidden.bs.modal', onHide);
    return () => {
      modal.removeEventListener('show.bs.modal', onShow);
      modal.removeEventListener('hidden.bs.modal', onHide);
    };
  }, []);

  // The banner needs the latest point as soon as a date / time is chosen, the chart when open.
  useEffect(() => {
    if (!symbol || !date) {
      setData([]);
      setStatus('idle');
      return undefined;
    }
    let cancelled = false;
    setStatus('loading');
    setData([]);
    PageServices.getFutureAndSpotData(symbol, date, time)
      .then((res) => {
        if (cancelled) return;
        const merged = mergeByMinute(res?.data?.futureData || [], res?.data?.spotData || []);
        setData(merged);
        setStatus(merged.length ? 'ready' : 'empty');
        const last = merged[merged.length - 1];
        onLatestRef.current?.(last ? { futurePrice: last.futurePrice, spotPrice: last.spotPrice } : null);
      })
      .catch(() => {
        if (cancelled) return;
        setStatus('empty');
        onLatestRef.current?.(null);
      });
    return () => {
      cancelled = true;
    };
  }, [symbol, date, time]);

  const toggle = (key) => setShow((prev) => ({ ...prev, [key]: !prev[key] }));
  const button = (key, label, on, off) => (
    <button className={`btn btn-sm ${show[key] ? on : off}`} onClick={() => toggle(key)}>{label}</button>
  );

  return (
    <div ref={modalRef} className="modal fade" id={modalId} tabIndex={-1} aria-labelledby={`${modalId}Label`} aria-hidden="true">
      <div className="modal-dialog modal-xl" style={{ maxWidth: '95vw', maxHeight: '90vh' }}>
        <div className="modal-content modal-bg-light-blue" style={{ height: '90vh' }}>
          <div className="modal-header">
            <h5 className="modal-title" id={`${modalId}Label`}>
              Future Price Graph{date ? ` · ${date}${time ? ` up to ${time}` : ''}` : ''}
            </h5>
            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
          </div>
          <div className="modal-body" style={{ height: '100%', overflowY: 'auto' }}>
            <div className="d-flex gap-2 mb-3 justify-content-center flex-wrap">
              {button('futureOI', 'Future OI', 'btn-primary', 'btn-outline-primary')}
              {button('futurePrice', 'Future Price', 'btn-primary', 'btn-outline-primary')}
              {button('spotPrice', 'Spot Price', 'btn-success', 'btn-outline-success')}
              {button('futureOIChange', 'Future OI Chng', 'btn-secondary', 'btn-outline-secondary')}
            </div>
            {open && data.length > 0 ? (
              <div style={{ height: '80vh' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="time" />
                    <YAxis yAxisId="left" orientation="left" stroke="purple" domain={['auto', 'auto']} tickFormatter={formatCompact} />
                    {show.futureOIChange && (
                      <YAxis yAxisId="oiChange" orientation="left" stroke="#9c27b0" domain={['auto', 'auto']} tickFormatter={formatCompact} />
                    )}
                    <YAxis yAxisId="right" orientation="right" stroke="blue" domain={['auto', 'auto']} tickFormatter={formatCompact} />
                    <Tooltip formatter={(value, name) => [formatCompact(value), name]} />
                    <Legend />
                    {show.spotPrice && <Line yAxisId="right" type="monotone" dataKey="spotPrice" stroke="green" strokeWidth={2} dot={false} name="Spot Price" />}
                    {show.futurePrice && <Line yAxisId="right" type="monotone" dataKey="futurePrice" stroke="blue" strokeWidth={2} dot={false} name="Future Price" />}
                    {show.futureOI && <Line yAxisId="left" type="monotone" dataKey="futureOI" stroke="purple" strokeWidth={2} dot={false} name="Future OI" />}
                    {show.futureOIChange && (
                      <Line yAxisId="oiChange" type="monotone" dataKey="futureOIChange" stroke="#9c27b0" strokeDasharray="4 2" strokeWidth={2} dot={false} name="Future OI Chng" />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-center text-muted py-5">
                {!date ? 'Select an expiry, date and time, then Fetch.' : status === 'loading' ? 'Loading…' : 'No data available'}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FuturePriceGraphModal;
